<template>
  <v-simple-table 
    ref="table"
    dense 
    fixed-header
    :height="`${tableHeight}px`"
    v-resize="resizeContainer"
  >
    <thead>
      <tr>
        <th :class="color"></th>
        <th 
          v-for="header in headers"
          class="text-center"
          :class="color"
          :key="`header${header}`"
        >
          {{ header }}
        </th>
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="feature in features"
        :key="`feature-${feature.name}`"
      >
        <td>{{ feature.name }}</td>
        <td 
          v-for="(val, i) in feature.vals"
          :key="`${feature.name}-${i}`"
          class="text-center"
        >
          {{ val != null ? val.toFixed(2) : val }}
        </td>
      </tr>
    </tbody>
  </v-simple-table>
</template>

<script>
import { getDegree } from '@js/utils/TrajectoryUtil.js';

export default {
  props: {
    posture: {
      required: true,
    },
    averagedPoses: {
      default: () => [],
    },
    currentPoses: {
      default: () => [],
    },
    poses: {
      default: () => [],
    },
    aspectRatio: {
      default: 1.92
    },
    color: {
      default: 'primary darken-4 white--text'
    }
  },
  data: function(){
    return {
      tableWidth: 0,
    }
  },
  computed: {
    headers: function(){
      return [
        '平均姿勢',
        this.posture.isVideo ? '現在の姿勢' : '選択中',
        ...(
          this.posture.isVideo
            ? ['最小', '最大', '範囲']
            : []
        )
      ];
    },
    tableHeight: function(){
      return parseInt(this.tableWidth/this.aspectRatio);
    },
    angles: function(){
      if(!this.posture.isVideo){
        return []
      }
      return this.poses.map(p => {
        const centerPositions = this.getCenterPositions(p);
        return [
          this.getJointAngle(p, 23, 27, 25),
          this.getJointAngle(p, 24, 28, 26),
          this.getJointAngle(p, 11, 15, 13),
          this.getJointAngle(p, 12, 16, 14),
          this.getJointAngle(p, 13, 23, 11), 
          this.getJointAngle(p, 14, 24, 12), 
          180 - this.getJointAngle(centerPositions, 0, 2, 1), 
        ];
      });
    },
    angleTimeSeries: function(){
      if(!this.posture.isVideo){
        return []
      }
      
      const rtn = [];
      for(let i=0; i<7; i++){
        rtn.push(this.angles.map(a => a[i]));
      }
      return rtn;
    },
    angleRanges: function(){
      const rtn = [];
      for(let i=0; i<7; i++){
        const min = Math.min(...this.angleTimeSeries[i]);
        const max = Math.max(...this.angleTimeSeries[i]);
        const range = max - min;
        rtn.push([
          min, max, range
        ]);
      }
      return rtn;
    },
    features: function(){
      const averagedCenterPositions = this.getCenterPositions(this.averagedPoses);
      const currentCenterPositions = this.getCenterPositions(this.currentPoses);
      return [
        {
          name: '左膝角度（°）',
          vals: [
            this.getJointAngle(this.averagedPoses, 23, 27, 25),
            this.getJointAngle(this.currentPoses, 23, 27, 25),
            ...(
              this.posture.isVideo ? this.angleRanges[0] : []
            )
          ]
        },
        {
          name: '右膝角度（°）',
          vals: [
            this.getJointAngle(this.averagedPoses, 24, 28, 26),
            this.getJointAngle(this.currentPoses, 24, 28, 26),
            ...(
              this.posture.isVideo ? this.angleRanges[1] : []
            )
          ]
        },
        {
          name: '左肘角度（°）',
          vals: [
            this.getJointAngle(this.averagedPoses, 11, 15, 13),
            this.getJointAngle(this.currentPoses, 11, 15, 13),
            ...(
              this.posture.isVideo ? this.angleRanges[2] : []
            )
          ]
        },
        {
          name: '右肘角度（°）',
          vals: [
            this.getJointAngle(this.averagedPoses, 12, 16, 14),
            this.getJointAngle(this.currentPoses, 12, 16, 14),
            ...(
              this.posture.isVideo ? this.angleRanges[3] : []
            )
          ]
        },
        {
          name: '左肩上がり角度（°）',
          vals: [
            this.getJointAngle(this.averagedPoses, 13, 23, 11), 
            this.getJointAngle(this.currentPoses, 13, 23, 11), 
            ...(
              this.posture.isVideo ? this.angleRanges[4] : []
            )
          ]
        },
        {
          name: '右肩上がり角度（°）',
          vals: [
            this.getJointAngle(this.averagedPoses, 14, 24, 12), 
            this.getJointAngle(this.currentPoses, 14, 24, 12), 
            ...(
              this.posture.isVideo ? this.angleRanges[5] : []
            )
          ]
        },
        {
          name: '首の曲がり角度（°）',
          vals: [
            180 - this.getJointAngle(averagedCenterPositions, 0, 2, 1), 
            180 - this.getJointAngle(currentCenterPositions, 0, 2, 1), 
            ...(
              this.posture.isVideo ? this.angleRanges[6] : []
            )
          ]
        },
      ];
    },
  },
  watch: {
    selection: {
      handler(){
        this.$forceUpdate();
      },
      deep: true,
      immediate: true,
    },
  },
  mounted: function(){
    this.$nextTick(() => {
      this.resizeContainer();
    })
  },
  methods:{
    resizeContainer: function(){
      this.$nextTick(() => {
        this.tableWidth = this.$refs.table?.$el?.clientWidth ?? 0;
      });
    },
    getJointAngle: function(poses, i, j, k){
      if(poses.length == 0){
        return null;
      }
      return getDegree(poses[i], poses[j], poses[k])
    },
    getCenterPositions: function(poses){
      if(poses.length == 0){
        return [];
      }

      const leftEar = poses[7];
      const rightEar = poses[8];

      const leftShoulder = poses[11];
      const rightShoulder = poses[12];

      const leftHip = poses[23];
      const rightHip = poses[24];

      return [
        {
          x: (leftEar.x + rightEar.x)/2,
          y: (leftEar.y + rightEar.y)/2,
          z: (leftEar.z + rightEar.z)/2,
        },
        {
          x: (leftShoulder.x + rightShoulder.x)/2,
          y: (leftShoulder.y + rightShoulder.y)/2,
          z: (leftShoulder.z + rightShoulder.z)/2,
        },
        {
          x: (leftHip.x + rightHip.x)/2,
          y: (leftHip.y + rightHip.y)/2,
          z: (leftHip.z + rightHip.z)/2,
        }
      ]
    },
  },
}
</script>

<style lang="scss" scoped>
tr:hover {
  background-color: transparent !important;
}
</style>